import { type ReactElement } from 'react';
import { RefreshCw } from 'lucide-react';
import { CredentialRotationDisabledReason } from '@pillar-portal/api-types';
import { Button } from '@/components/ui/button';

const ROTATION_DISABLED_REASON_COPY: Record<CredentialRotationDisabledReason, string> = {
    [CredentialRotationDisabledReason.NO_EFFECTIVE_CREDENTIAL]:
        'Create a credential before rotating it.',
    [CredentialRotationDisabledReason.MISSING_USE_PERMISSION]:
        'You do not have permission to use this website credential.',
    [CredentialRotationDisabledReason.MISSING_ROTATE_PERMISSION]:
        'You do not have permission to rotate this website credential.',
    [CredentialRotationDisabledReason.MISSING_AUDIT_PERMISSION]:
        'You do not have permission to view rotation history.',
    [CredentialRotationDisabledReason.NOT_ADMIN_FOR_ORGANIZATION]:
        'Only an authorized administrator can view rotation history.',
    [CredentialRotationDisabledReason.NO_ROTATION_FLOW]:
        'No credential rotation flow is configured.',
    [CredentialRotationDisabledReason.NO_ROTATABLE_SECRET_FIELDS]:
        'No secret credential fields are configured for rotation.',
    [CredentialRotationDisabledReason.ACTIVE_ATTEMPT_EXISTS]:
        'A credential rotation is already in progress.',
    [CredentialRotationDisabledReason.INVALID_GENERATION_POLICY]:
        'Credential generation rules are incomplete.',
    [CredentialRotationDisabledReason.APP_RUNTIME_REQUIRED]:
        'Open this website in the Anchorbase App to rotate credentials.',
};

export interface IpxCredentialRotationButtonProps {
    readonly canRotate: boolean;
    readonly disabledReason: CredentialRotationDisabledReason | null;
    readonly onRotate?: () => void;
}

export function getCredentialRotationDisabledReasonCopy(
    reason: CredentialRotationDisabledReason | null,
    fallbackReason: CredentialRotationDisabledReason
): string {
    return ROTATION_DISABLED_REASON_COPY[reason ?? fallbackReason];
}

export function IpxCredentialRotationButton(props: IpxCredentialRotationButtonProps): ReactElement {
    const hasRuntimeHandler = props.onRotate !== undefined;
    const isDisabled = !props.canRotate || !hasRuntimeHandler;
    const disabledReason = getCredentialRotationDisabledReasonCopy(
        props.disabledReason,
        CredentialRotationDisabledReason.APP_RUNTIME_REQUIRED
    );
    const title = isDisabled ? disabledReason : 'Rotate credential';
    return (
        <span className="inline-flex" title={title}>
            <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isDisabled}
                title={title}
                onClick={() => {
                    if (isDisabled) {
                        return;
                    }
                    props.onRotate?.();
                }}
            >
                <RefreshCw className="mr-2 h-4 w-4" />
                Rotate
            </Button>
        </span>
    );
}
